import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../components/ui/input';
import { formatInputDisplay } from '../utils/formatters';
import { getLanguage, trDisplay } from './index';

export function parseLocalizedNumber(value, language = getLanguage()) {
  if (value == null || value === '') return null;
  let text = String(value).replace(/[€%\s\u00a0]/g, '').replace(/^−/, '-');
  const english = language === 'en' || language.startsWith('en-');
  if (english) {
    if (!/^[+-]?(?:(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?|\.\d+)$/.test(text)) return null;
    text = text.replace(/,/g, '');
  } else {
    if (!/^[+-]?(?:(?:\d+|\d{1,3}(?:\.\d{3})+)(?:,\d+)?|,\d+)$/.test(text)) return null;
    text = text.replace(/\./g, '').replace(',', '.');
  }
  const result = Number(text);
  return Number.isFinite(result) ? result : null;
}

// The editing locale belongs to the draft, never to the financial value.
export default function LocalizedNumberInput({ value, onChange, onDraftChange, onCommit,
  decimals = 0, isPercent = false, commitOnBlur = false, invalidAsNaN = false,
  editingOnly = false, type: ignoredType, ...props }) {
  useTranslation();
  const language = getLanguage();
  const editLanguage = useRef(language), focused = useRef(false), pending = useRef(false);
  const previousValue = useRef(value);
  const formatted = v => v == null || v === '' || !Number.isFinite(Number(v)) ? ''
    : editingOnly ? String(v).replace('.', language === 'it' ? ',' : '.')
      : formatInputDisplay(v, { decimals, isPercent });
  const [draft, setDraft] = useState(() => formatted(value));
  const draftRef = useRef(draft), inputRef = useRef(null);
  const incomplete = pending.current && draft !== '' && parseLocalizedNumber(draft, editLanguage.current) == null;
  useEffect(() => { inputRef.current?.setCustomValidity(incomplete ? trDisplay('Completa il numero prima di continuare.') : ''); }, [incomplete, language]);
  const setText = text => { draftRef.current = text; setDraft(text); };
  useEffect(() => {
    // An external restore/reset replaces a draft; changing language alone does not.
    if (!Object.is(previousValue.current, value)) {
      previousValue.current = value;
      if (!focused.current) { pending.current = false; setText(formatted(value)); editLanguage.current = language; }
    }
    if (!focused.current && !pending.current) { setText(formatted(value)); editLanguage.current = language; }
    else if (editLanguage.current !== language) {
      const parsed = parseLocalizedNumber(draftRef.current, editLanguage.current);
      if (parsed != null) { setText(String(parsed).replace('.', language === 'it' ? ',' : '.')); editLanguage.current = language; }
    }
    // Formatting is a view update, and deliberately never invokes onChange.
  }, [value, language, decimals, isPercent]);
  return <Input {...props} ref={inputRef} aria-invalid={incomplete || props['aria-invalid']} type="text" inputMode="decimal" value={draft}
    onFocus={() => {
      focused.current = true;
      if (!pending.current) { editLanguage.current = language; setText(value == null || value === '' || !Number.isFinite(Number(value)) ? '' : String(value).replace('.', language === 'it' ? ',' : '.')); }
    }}
    onChange={event => {
      const text = event.target.value; pending.current = true; setText(text);
      const parsed = parseLocalizedNumber(text, editLanguage.current);
      onDraftChange?.(parsed, text);
      if (!commitOnBlur) {
        if (text === '') onChange('');
        else if (parsed != null) onChange(parsed);
        else if (invalidAsNaN) onChange(NaN);
      }
    }}
    onBlur={event => {
      focused.current = false;
      if (event.relatedTarget?.closest?.('[data-language-control]')) return;
      const text = draftRef.current, parsed = parseLocalizedNumber(text, editLanguage.current);
      if (parsed != null || text === '') {
        pending.current = false;
        onChange(text === '' ? '' : parsed);
        editLanguage.current = language; setText(formatted(text === '' ? '' : parsed));
        onCommit?.();
      }
    }} />;
}
